import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { ApiService } from './api.service';
import { AppStateService } from './app-state.service';
import { Poster, PurchasePosterResponse, VaultResponse } from '../../models';

const VAULT_KEY = 'cinephile_vault';

@Injectable({ providedIn: 'root' })
export class VaultService {
  constructor(
    private api: ApiService,
    private state: AppStateService
  ) {}

  getAllPosters(): Observable<{ posters: Poster[] }> {
    return this.getUserVault(this.state.userId).pipe(
      map((vault) => ({ posters: vault.posters }))
    );
  }

  getUserVault(userId: string): Observable<VaultResponse> {
    return this.api.get<VaultResponse>(`/api/users/${userId}/vault`).pipe(
      catchError(() => of(this.readLocalVault(userId)))
    );
  }

  purchasePoster(posterId: string): Observable<PurchasePosterResponse> {
    const userId = this.state.userId;
    return this.api.post<PurchasePosterResponse>(
      `/api/users/${userId}/purchase-poster`,
      { poster_id: posterId }
    ).pipe(
      tap((res) => {
        this.state.updateUserTickets(res.new_balance);
      }),
      catchError(() => this.purchasePosterLocally(posterId, userId))
    );
  }

  getRarityColor(rarity: string): string {
    const map: Record<string, string> = {
      common: '#7A7468',
      rare: '#C9A84C',
      legendary: '#E8C97A'
    };
    return map[rarity] ?? '#7A7468';
  }

  getRarityLabel(rarity: string): string {
    const map: Record<string, string> = {
      common: '✦ Common',
      rare: '✦✦ Rare',
      legendary: '✦✦✦ Legendary'
    };
    return map[rarity] ?? rarity;
  }

  private purchasePosterLocally(posterId: string, userId: string): Observable<PurchasePosterResponse> {
    const vault = this.readLocalVault(userId);
    const poster = vault.posters.find((p) => p.poster_id === posterId);
    const currentBalance = this.state.user?.tickets ?? 0;

    if (!poster || poster.is_unlocked || currentBalance < poster.ticket_cost) {
      return of({
        message: 'Purchase failed',
        poster: poster ?? this.localPosters()[0],
        tickets_spent: 0,
        new_balance: currentBalance
      });
    }

    const newBalance = currentBalance - poster.ticket_cost;
    poster.is_unlocked = true;
    poster.earned_via = 'ticket_purchase';
    poster.earned_at = new Date().toISOString();
    this.writeLocalVault(userId, vault.posters);
    this.state.updateUserTickets(newBalance);

    return of({
      message: 'Poster unlocked',
      poster,
      tickets_spent: poster.ticket_cost,
      new_balance: newBalance
    });
  }

  private readLocalVault(userId: string): VaultResponse {
    const posters = this.localPosters();
    const unlockedMap = this.readLocalUnlockMap(userId);
    const merged = posters.map((poster) => ({
      ...poster,
      is_unlocked: Boolean(unlockedMap[poster.poster_id]),
      earned_via: unlockedMap[poster.poster_id] ? 'ticket_purchase' : undefined
    }));
    return {
      total: merged.length,
      unlocked: merged.filter((p) => p.is_unlocked).length,
      locked: merged.filter((p) => !p.is_unlocked).length,
      posters: merged
    };
  }

  private writeLocalVault(userId: string, posters: Poster[]): void {
    if (typeof localStorage === 'undefined') return;
    const data = this.readAllVaultData();
    data[userId] = posters.filter((p) => p.is_unlocked).reduce<Record<string, true>>((acc, poster) => {
      acc[poster.poster_id] = true;
      return acc;
    }, {});
    localStorage.setItem(VAULT_KEY, JSON.stringify(data));
  }

  private readLocalUnlockMap(userId: string): Record<string, true> {
    const data = this.readAllVaultData();
    return data[userId] ?? {};
  }

  private readAllVaultData(): Record<string, Record<string, true>> {
    if (typeof localStorage === 'undefined') return {};
    const raw = localStorage.getItem(VAULT_KEY);
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  private localPosters(): Poster[] {
    return [
      {
        poster_id: 'vault-noir-echo',
        title: 'Noir Echoes',
        year: 1948,
        genre: 'Neo Noir',
        rarity: 'legendary',
        ticket_cost: 120,
        bg_gradient: 'linear-gradient(135deg,#0a0a0f,#1f1f2e,#09090c)',
        is_active: true
      },
      {
        poster_id: 'vault-cosmic-cut',
        title: 'Cosmic Cut',
        year: 1984,
        genre: 'Sci-Fi',
        rarity: 'rare',
        ticket_cost: 80,
        bg_gradient: 'linear-gradient(135deg,#03143a,#123a7a,#0b1e46)',
        is_active: true
      },
      {
        poster_id: 'vault-red-curtain',
        title: 'Red Curtain',
        year: 1972,
        genre: 'Drama',
        rarity: 'rare',
        ticket_cost: 65,
        bg_gradient: 'linear-gradient(135deg,#2b0707,#6d1212,#2f0505)',
        is_active: true
      },
      {
        poster_id: 'vault-dust-light',
        title: 'Dust & Light',
        year: 1961,
        genre: 'Classic',
        rarity: 'common',
        ticket_cost: 35,
        bg_gradient: 'linear-gradient(135deg,#28201a,#59452f,#30241a)',
        is_active: true
      },
      {
        poster_id: 'vault-midnight-track',
        title: 'Midnight Track',
        year: 1991,
        genre: 'Thriller',
        rarity: 'common',
        ticket_cost: 30,
        bg_gradient: 'linear-gradient(135deg,#0c0f18,#1f2d4f,#0d1628)',
        is_active: true
      },
      {
        poster_id: 'vault-vintage-frames',
        title: 'Vintage Frames',
        year: 1955,
        genre: 'World Cinema',
        rarity: 'common',
        ticket_cost: 25,
        bg_gradient: 'linear-gradient(135deg,#3b311f,#6f5a35,#40311f)',
        is_active: true
      }
    ];
  }
}
